import { Loader2, Trash2 } from "lucide-react";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ChronologyEvent, ChronologyEventOrigin } from "~/api/model";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { formatChronologyEventDate } from "../helpers/chronologyRequest";
import { ChronologyEditedBadge } from "./ChronologyEditedBadge";
import { ChronologyReviewStatusBadge } from "./ChronologyReviewStatusBadge";
import { ChronologySourceBadge } from "./ChronologySourceBadge";

interface ChronologyDuplicateEventProps {
  event: ChronologyEvent;
  /** Asks the parent to confirm and delete this event. */
  onDelete: (event: ChronologyEvent) => void;
  isDeleting: boolean;
  /** Disables the delete control while any deletion is in flight. */
  disabled: boolean;
}

/**
 * A read-only rendering of a single chronology event inside a duplicate group.
 * Mirrors the fields shown in the main chronology row, plus a single manual
 * Delete action — duplicate cleanup is always an explicit, per-event choice.
 */
export const ChronologyDuplicateEvent: FC<ChronologyDuplicateEventProps> = ({
  event,
  onDelete,
  isDeleting,
  disabled,
}) => {
  const intl = useIntl();

  const displayDate = formatChronologyEventDate(event);
  const metadata = [event.provider, event.facility, event.documentCategory]
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const { pageNumber, batesNumber } = event.reference;
  const sourceText = batesNumber
    ? batesNumber
    : pageNumber != null
      ? intl.formatMessage(
          { id: "chronology.source.page" },
          { page: pageNumber + 1 }
        )
      : null;
  // A per-event accessible name so screen-reader and voice-control users can tell
  // which row's delete control they're on. Source context (page/Bates) is always
  // appended so two rows that share a date and detail but come from different
  // pages don't collapse to the same label; a blank-detail row (one that only
  // groups on matching metadata) falls back to type/metadata context first.
  const detail = event.detail.trim();
  const descriptorParts = detail
    ? [detail, sourceText]
    : [event.eventType, ...metadata, sourceText];
  const eventDescriptor = descriptorParts
    .map((value) => value?.trim() ?? "")
    .filter((value) => value.length > 0)
    .join(" · ");
  const deleteLabel = intl.formatMessage(
    { id: "chronology.duplicates.deleteEvent" },
    {
      event: displayDate
        ? `${displayDate} — ${eventDescriptor}`
        : eventDescriptor,
    }
  );

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {displayDate ?? (
              <span className="text-muted-foreground">
                <FormattedMessage id="chronology.list.undatedLabel" />
              </span>
            )}
          </span>
          <ChronologyEditedBadge event={event} className="h-5 text-[10px]" />
          <ChronologyReviewStatusBadge status={event.reviewStatus} />
          {event.origin === ChronologyEventOrigin.MANUAL && (
            <Badge variant="outline" className="h-5 text-[10px]">
              <FormattedMessage id="chronology.badge.manual" />
            </Badge>
          )}
          <ChronologySourceBadge reference={event.reference} />
        </div>

        {event.eventType.trim().length > 0 && (
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {event.eventType}
          </span>
        )}

        <p className="text-sm text-foreground">{event.detail}</p>

        {metadata.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {metadata.join(" · ")}
          </p>
        )}
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            disabled={disabled}
            aria-label={deleteLabel}
            onClick={() => onDelete(event)}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <FormattedMessage id="chronology.action.delete" />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
